// ============================================================
// PaettAI (பேட்டை) — PRICING CONFIGURATION
// Single source of truth for all tiers, limits, and billing rules
// Location: src/config/pricing.js
// ============================================================

export const CURRENCY = '₹';
export const CURRENCY_CODE = 'INR';

// ============================================================
// PLAN TIERS
// ============================================================
export const PLANS = {
    starter: {
        id: 'starter',
        name: 'Starter',
        tagline: 'Solo founders finding their voice.',
        monthlyPrice: 14999,
        annualPrice: 149990, // 2 months free
        annualMonthly: 12499, // what it works out to per month

        // Credits
        monthlyCredits: 500,
        creditsCarryOver: false, // unused credits do NOT roll over

        // Feature limits
        maxUsers: 1,
        maxDnaProfiles: 1,
        maxContentHubs: 3,
        availableHubs: ['hr', 'finance', 'sales', 'marketing', 'tech', 'operations'], // can pick any 3
        analyticsAccess: false,
        brandKitAccess: true,
        teamManagement: false,
        apiAccess: false,
        whiteLabel: false,
        customIntegrations: false,
        dedicatedAccountManager: false,

        // Support
        supportTier: 'email',
        supportResponseHours: 48,

        // Credit costs per content type
        creditCosts: {
            shortText: 1,        // Social post, email, JD, short copy (~500 words)
            longText: 2,         // Article, report, case study (~1000 words)
            longFormText: 3,     // Whitepaper, thought leadership (~2000 words)
            imageStandard: 2,    // DALL-E 3 standard image
            imageHD: 3,          // DALL-E 3 HD image
            pptSmall: 20,        // PPT deck 10 slides + images
            pptLarge: 35,        // PPT deck 20 slides + images
            videoScript: 3,      // Video script + storyboard
        },

        // AI Model access
        aiModels: ['gemini'],   // Starter gets Gemini only
        maxTokensPerGeneration: 4000,
    },

    growth: {
        id: 'growth',
        name: 'Growth',
        tagline: 'Scaling teams with one founder voice.',
        monthlyPrice: 34999,
        annualPrice: 349990,
        annualMonthly: 29166,
        isPopular: true,

        // Credits
        monthlyCredits: 2500,
        creditsCarryOver: false,

        // Feature limits
        maxUsers: 5,
        maxDnaProfiles: 1,
        maxContentHubs: 6,       // All 6 hubs
        availableHubs: ['hr', 'finance', 'sales', 'marketing', 'tech', 'operations'],
        analyticsAccess: true,
        brandKitAccess: true,
        teamManagement: true,
        apiAccess: false,
        whiteLabel: false,
        customIntegrations: false,
        dedicatedAccountManager: false,

        // Support
        supportTier: 'priority',
        supportResponseHours: 12,

        // Credit costs (same structure, same costs)
        creditCosts: {
            shortText: 1,
            longText: 2,
            longFormText: 3,
            imageStandard: 2,
            imageHD: 3,
            pptSmall: 20,
            pptLarge: 35,
            videoScript: 3,
        },

        // AI Model access
        aiModels: ['gemini', 'claude', 'gpt4o'],  // Pro gets all 3
        maxTokensPerGeneration: 8000,
    },

    enterprise: {
        id: 'enterprise',
        name: 'Enterprise',
        tagline: 'Organizations at scale.',
        monthlyPrice: 74999,
        annualPrice: 749990,
        annualMonthly: 62499,

        // Credits
        monthlyCredits: Infinity,  // Unlimited
        creditsCarryOver: false,

        // Feature limits
        maxUsers: 15,
        maxDnaProfiles: 5,          // Multiple founder profiles
        maxContentHubs: 6,
        availableHubs: ['hr', 'finance', 'sales', 'marketing', 'tech', 'operations'],
        analyticsAccess: true,
        brandKitAccess: true,
        teamManagement: true,
        apiAccess: true,
        whiteLabel: true,
        customIntegrations: true,
        dedicatedAccountManager: true,

        // Support
        supportTier: 'dedicated',
        supportResponseHours: 4,
        slaUptime: 99.9,

        // Credit costs
        creditCosts: {
            shortText: 1,
            longText: 2,
            longFormText: 3,
            imageStandard: 2,
            imageHD: 3,
            pptSmall: 20,
            pptLarge: 35,
            videoScript: 3,
        },

        // AI Model access
        aiModels: ['gemini', 'claude', 'gpt4o'],
        maxTokensPerGeneration: 16000,
    },
};

// ============================================================
// TOP-UP CREDIT PACKS
// ============================================================
export const TOP_UP_PACKS = [
    {
        id: 'topup_50',
        credits: 50,
        prices: {
            starter: 1999,
            growth: 1799,
            enterprise: 1499,
        },
        label: '50 Credits',
    },
    {
        id: 'topup_100',
        credits: 100,
        prices: {
            starter: 3499,
            growth: 2999,
            enterprise: 2499,
        },
        label: '100 Credits',
        popular: true,
    },
    {
        id: 'topup_250',
        credits: 250,
        prices: {
            starter: 7999,
            growth: 6999,
            enterprise: 5999,
        },
        label: '250 Credits',
    },
];

// ============================================================
// CONTENT TYPE DEFINITIONS
// Maps to creditCosts keys above
// ============================================================
export const CONTENT_TYPES = {
    shortText: {
        id: 'shortText',
        label: 'Short Text',
        description: 'Social post, email, JD, short copy (~500 words)',
        credits: 1,
        estimatedWords: 500,
        hubs: ['hr', 'finance', 'sales', 'marketing', 'tech', 'operations'],
        icon: '📝',
    },
    longText: {
        id: 'longText',
        label: 'Long Text',
        description: 'Article, report, case study (~1000 words)',
        credits: 2,
        estimatedWords: 1000,
        hubs: ['hr', 'finance', 'sales', 'marketing', 'tech', 'operations'],
        icon: '📄',
    },
    longFormText: {
        id: 'longFormText',
        label: 'Long-form Text',
        description: 'Whitepaper, thought leadership (~2000 words)',
        credits: 3,
        estimatedWords: 2000,
        hubs: ['marketing', 'tech', 'finance'],
        icon: '📑',
    },
    imageStandard: {
        id: 'imageStandard',
        label: 'Standard Image',
        description: 'Social media graphic, blog image (1024×1024)',
        credits: 2,
        hubs: ['marketing', 'sales'],
        icon: '🖼️',
    },
    imageHD: {
        id: 'imageHD',
        label: 'HD Image',
        description: 'Presentation visual, banner (1024×1792)',
        credits: 3,
        hubs: ['marketing', 'sales'],
        icon: '🎨',
    },
    pptSmall: {
        id: 'pptSmall',
        label: 'PPT Deck (10 slides)',
        description: 'Presentation with slide texts + images',
        credits: 20,
        hubs: ['finance', 'sales', 'marketing', 'tech'],
        icon: '📊',
    },
    pptLarge: {
        id: 'pptLarge',
        label: 'PPT Deck (20 slides)',
        description: 'Full presentation with detailed content + images',
        credits: 35,
        hubs: ['finance', 'sales', 'marketing', 'tech'],
        icon: '📈',
    },
    videoScript: {
        id: 'videoScript',
        label: 'Video Script',
        description: 'Script + storyboard breakdown',
        credits: 3,
        hubs: ['marketing', 'sales', 'hr'],
        icon: '🎬',
    },
};

// ============================================================
// HUB-SPECIFIC CONTENT TYPES
// Which content types each hub can generate
// ============================================================
export const HUB_CONTENT_MAP = {
    hr: {
        name: 'HR Hub',
        types: [
            { type: 'shortText', label: 'Job Description' },
            { type: 'longText', label: 'Culture Document' },
            { type: 'shortText', label: 'Internal Communication' },
            { type: 'longText', label: 'Onboarding Content' },
            { type: 'longText', label: 'Employee Handbook Section' },
            { type: 'shortText', label: 'Recruitment Campaign' },
            { type: 'videoScript', label: 'Employer Brand Video Script' },
        ],
    },
    finance: {
        name: 'Finance Hub',
        types: [
            { type: 'longText', label: 'Investor Update' },
            { type: 'longText', label: 'Quarterly Report' },
            { type: 'pptSmall', label: 'Financial Presentation' },
            { type: 'shortText', label: 'Stakeholder Letter' },
            { type: 'pptLarge', label: 'Fundraising Deck' },
            { type: 'longFormText', label: 'Annual Report Section' },
        ],
    },
    sales: {
        name: 'Sales Hub',
        types: [
            { type: 'pptSmall', label: 'Pitch Deck' },
            { type: 'longText', label: 'Case Study' },
            { type: 'shortText', label: 'Outreach Email' },
            { type: 'longText', label: 'Proposal Narrative' },
            { type: 'longText', label: 'Customer Success Story' },
            { type: 'shortText', label: 'Sales Script' },
            { type: 'imageStandard', label: 'Sales Collateral Image' },
        ],
    },
    marketing: {
        name: 'Marketing Hub',
        types: [
            { type: 'shortText', label: 'Social Media Post' },
            { type: 'longText', label: 'Blog Article' },
            { type: 'longText', label: 'Brand Campaign Brief' },
            { type: 'longFormText', label: 'Thought Leadership Piece' },
            { type: 'shortText', label: 'Advertising Copy' },
            { type: 'longText', label: 'PR Narrative' },
            { type: 'shortText', label: 'Newsletter Content' },
            { type: 'imageStandard', label: 'Social Media Graphic' },
            { type: 'imageHD', label: 'Campaign Banner' },
            { type: 'videoScript', label: 'Marketing Video Script' },
        ],
    },
    tech: {
        name: 'Tech Hub',
        types: [
            { type: 'longText', label: 'Technical Roadmap' },
            { type: 'longText', label: 'Product Documentation' },
            { type: 'longText', label: 'Innovation Showcase' },
            { type: 'longFormText', label: 'R&D Report' },
            { type: 'shortText', label: 'Engineering Manifesto' },
            { type: 'longFormText', label: 'Patent Narrative' },
        ],
    },
    operations: {
        name: 'Operations Hub',
        types: [
            { type: 'longText', label: 'Operations Update' },
            { type: 'shortText', label: 'Retail Content' },
            { type: 'longText', label: 'Legal Communication' },
            { type: 'longText', label: 'Compliance Document' },
            { type: 'shortText', label: 'E-commerce Narrative' },
            { type: 'longText', label: 'Policy Document' },
        ],
    },
};

// ============================================================
// REMINDER & NOTIFICATION THRESHOLDS
// Used by dashboard and email notification system
// ============================================================
export const CREDIT_ALERTS = {
    // Percentage thresholds (of monthly credits)
    warningThreshold: 0.25,     // 25% remaining — yellow warning on dashboard
    criticalThreshold: 0.10,    // 10% remaining — red alert + email reminder
    exhaustedThreshold: 0,      // 0% — block generation + show top-up prompt

    // Dashboard badge colors
    colors: {
        healthy: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', label: 'Healthy' },
        warning: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: 'Running Low' },
        critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: 'Almost Exhausted' },
        exhausted: { bg: 'bg-red-200', text: 'text-red-800', border: 'border-red-500', label: 'Credits Exhausted' },
    },

    // Email reminders (sent once per threshold crossing)
    emailReminders: {
        at50Percent: {
            subject: 'You\'ve used 50% of your monthly credits',
            trigger: 0.50,
            sendOnce: true,
        },
        at75Percent: {
            subject: 'Only 25% of your credits remaining this month',
            trigger: 0.75,
            sendOnce: true,
        },
        at90Percent: {
            subject: '⚠️ Only 10% credits remaining — top up now',
            trigger: 0.90,
            sendOnce: true,
        },
        exhausted: {
            subject: '🚨 Credits exhausted — top up to continue generating',
            trigger: 1.0,
            sendOnce: true,
        },
    },
};

// ============================================================
// BILLING CYCLE CONFIGURATION
// ============================================================
export const BILLING = {
    // Cycle
    billingCycleDay: 1,           // Credits reset on 1st of each month
    gracePeriodDays: 3,           // 3 days after payment failure before downgrade
    trialDays: 0,                 // No free trial (DNA assessment is free)

    // Payment methods
    supportedPayments: ['razorpay', 'stripe', 'bank_transfer'],
    defaultGateway: 'razorpay',

    // Invoice
    gstRate: 0.18,                // 18% GST for India
    invoicePrefix: 'PAI',         // Invoice numbers: PAI-2026-001

    // Subscription states
    states: {
        ACTIVE: 'active',
        PAST_DUE: 'past_due',     // Payment failed, in grace period
        CANCELLED: 'cancelled',
        PAUSED: 'paused',
        TRIAL: 'trial',
    },

    // What happens on downgrade / cancellation
    onCancel: {
        keepData: true,           // User data preserved
        keepDnaProfile: true,     // DNA profile preserved forever
        contentAccess: 'read_only', // Can view but not generate
        daysUntilDataDeletion: 365, // Data deleted after 1 year of inactivity
    },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get plan details by ID
 */
export const getPlan = (planId) => PLANS[planId] || null;

/**
 * Get credit cost for a content type under a specific plan
 */
export const getCreditCost = (planId, contentType) => {
    const plan = PLANS[planId];
    if (!plan) return null;
    return plan.creditCosts[contentType] || null;
};

/**
 * Check if user can generate content (has enough credits)
 */
export const canGenerate = (creditsRemaining, contentType, planId) => {
    const cost = getCreditCost(planId, contentType);
    if (cost === null) return false;
    if (PLANS[planId]?.monthlyCredits === Infinity) return true; // Enterprise unlimited
    return creditsRemaining >= cost;
};

/**
 * Calculate credit status and alert level
 */
export const getCreditStatus = (creditsUsed, planId) => {
    const plan = PLANS[planId];
    if (!plan) return null;

    const totalCredits = plan.monthlyCredits;
    if (totalCredits === Infinity) {
        return { status: 'healthy', ...CREDIT_ALERTS.colors.healthy, remaining: Infinity, percentUsed: 0 };
    }

    const remaining = Math.max(0, totalCredits - creditsUsed);
    const percentUsed = creditsUsed / totalCredits;
    const percentRemaining = remaining / totalCredits;

    let status;
    if (remaining <= 0) {
        status = { status: 'exhausted', ...CREDIT_ALERTS.colors.exhausted };
    } else if (percentRemaining <= CREDIT_ALERTS.criticalThreshold) {
        status = { status: 'critical', ...CREDIT_ALERTS.colors.critical };
    } else if (percentRemaining <= CREDIT_ALERTS.warningThreshold) {
        status = { status: 'warning', ...CREDIT_ALERTS.colors.warning };
    } else {
        status = { status: 'healthy', ...CREDIT_ALERTS.colors.healthy };
    }

    return {
        ...status,
        remaining,
        totalCredits,
        creditsUsed,
        percentUsed: Math.round(percentUsed * 100),
        percentRemaining: Math.round(percentRemaining * 100),
    };
};

/**
 * Get top-up price for a specific pack and plan
 */
export const getTopUpPrice = (packId, planId) => {
    const pack = TOP_UP_PACKS.find(p => p.id === packId);
    if (!pack) return null;
    return pack.prices[planId] || pack.prices.starter; // fallback to starter price
};

/**
 * Check if a hub is available for a plan
 */
export const isHubAvailable = (planId, hubId, selectedHubs = []) => {
    const plan = PLANS[planId];
    if (!plan) return false;

    // Enterprise and Growth get all hubs
    if (plan.maxContentHubs === 6) return true;

    // Starter: check if hub is in user's selected hubs
    return selectedHubs.includes(hubId);
};

/**
 * Check if user has reached their user seat limit
 */
export const canAddUser = (planId, currentUserCount) => {
    const plan = PLANS[planId];
    if (!plan) return false;
    return currentUserCount < plan.maxUsers;
};

/**
 * Calculate invoice amount with GST
 */
export const calculateInvoice = (planId, isAnnual = false) => {
    const plan = PLANS[planId];
    if (!plan) return null;

    const baseAmount = isAnnual ? plan.annualPrice : plan.monthlyPrice;
    const gst = Math.round(baseAmount * BILLING.gstRate);
    const total = baseAmount + gst;

    return {
        planName: plan.name,
        period: isAnnual ? 'Annual' : 'Monthly',
        baseAmount,
        gstRate: BILLING.gstRate,
        gstAmount: gst,
        totalAmount: total,
        currency: CURRENCY_CODE,
    };
};

/**
 * Get which email reminders should fire based on usage
 */
export const getTriggeredReminders = (creditsUsed, totalCredits, alreadySent = []) => {
    if (totalCredits === Infinity) return []; // Enterprise never gets reminders

    const percentUsed = creditsUsed / totalCredits;
    const reminders = [];

    Object.entries(CREDIT_ALERTS.emailReminders).forEach(([key, config]) => {
        if (percentUsed >= config.trigger && !alreadySent.includes(key)) {
            reminders.push({ key, ...config });
        }
    });

    return reminders;
};

/**
 * Format price with currency symbol
 */
export const formatPrice = (amount) => {
    if (amount === Infinity) return 'Unlimited';
    return `${CURRENCY}${amount.toLocaleString('en-IN')}`;
};

/**
 * Get plan comparison data for pricing page
 */
export const getPlanComparison = () => {
    return Object.values(PLANS).map(plan => ({
        id: plan.id,
        name: plan.name,
        tagline: plan.tagline,
        monthlyPrice: plan.monthlyPrice,
        monthlyCredits: plan.monthlyCredits,
        maxUsers: plan.maxUsers,
        maxContentHubs: plan.maxContentHubs,
        supportTier: plan.supportTier,
        supportResponseHours: plan.supportResponseHours,
        analyticsAccess: plan.analyticsAccess,
        teamManagement: plan.teamManagement,
        apiAccess: plan.apiAccess,
        whiteLabel: plan.whiteLabel,
        isPopular: plan.isPopular || false,
    }));
};

export default PLANS;